import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchGlobal } from "../lib/api";
import { useT } from "../i18n/I18nProvider";

const HERO_IMG = "https://images.pexels.com/photos/12801668/pexels-photo-12801668.jpeg";

export default function Hero() {
  const { t, lp } = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const tm = setTimeout(async () => {
      try { const data = await searchGlobal(q); setResults(data.results || []); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(tm);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (results[0]) {
      const r = results[0];
      navigate(r.type === "city" ? lp(`/villes/${r.slug}`) : lp(`/${r.slug}`));
    }
  };

  return (
    <section className="relative h-[600px] md:h-[680px] overflow-hidden" data-testid="hero">
      <img src={HERO_IMG} alt="Skyline polonais" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 hej-hero-overlay" />
      <div className="relative h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center text-white">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-medium mb-6 animate-fade-up" data-testid="hero-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          {t("hero.chip")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl animate-fade-up" data-testid="hero-title">
          {t("hero.title")}
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-200 leading-relaxed animate-fade-up" style={{ animationDelay: "0.15s" }} data-testid="hero-subtitle">
          {t("hero.subtitle")}
        </p>

        <form onSubmit={submit} className="w-full max-w-2xl mt-10 animate-fade-up relative" style={{ animationDelay: "0.3s" }} data-testid="hero-search">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-2 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("hero.searchPlaceholder")}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 outline-none"
                data-testid="hero-search-input"
              />
            </div>
            <button type="submit" className="bg-[#0056D2] hover:bg-[#0047AD] text-white font-semibold px-6 py-3.5 rounded-xl transition" data-testid="hero-search-button">
              {t("hero.explore")}
            </button>
          </div>
          {results.length > 0 && (
            <div className="absolute top-full mt-2 left-2 right-2 bg-white rounded-xl shadow-2xl overflow-hidden text-left" data-testid="hero-search-results">
              {results.slice(0, 6).map((r, i) => (
                <Link key={i} to={r.type === "city" ? lp(`/villes/${r.slug}`) : lp(`/${r.slug}`)} className="block px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`hero-result-${r.slug}`}>
                  <div className="text-sm font-medium text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-500">{r.subtitle}</div>
                </Link>
              ))}
            </div>
          )}
        </form>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-sm text-slate-300 animate-fade-up" style={{ animationDelay: "0.4s" }}>
          <span>{t("hero.statCities")}</span>
          <span className="w-1 h-1 rounded-full bg-slate-500"></span>
          <span>{t("hero.statNbp")}</span>
          <span className="w-1 h-1 rounded-full bg-slate-500"></span>
          <span>{t("hero.statRgpd")}</span>
        </div>
      </div>
    </section>
  );
}
